import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Collapse,
  Autocomplete,
  Select,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SwapVertIcon from '@mui/icons-material/SwapVert';

const fieldSizing = {
  '& .MuiInputBase-root': { borderRadius: 1 },
};

const LeadFilters = ({
  showFilters,
  filters,
  sort,
  noActivity,
  statusOptions,
  statusOptionsLoading = false,
  onOpenStatusOptions,
  usersList,
  usersListLoading = false,
  onOpenUsersList,
  onFilterChange,
  onSortChange,
  onNoActivityChange,
  onClearFilters,
}) => {
  return (
    <Collapse in={showFilters}>
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: (theme) => theme.palette.mode === 'light' ? '0 1px 3px rgba(15,23,42,0.06)' : 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          sx={{
            px: 2.5,
            py: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneIcon color="primary" sx={{ fontSize: 22, opacity: 0.85 }} />
              <Typography component="span" variant="subtitle1" fontWeight={600}>
                Filters & sorting
              </Typography>
            </Box>
          }
          action={
            <Button
              onClick={onClearFilters}
              color="primary"
              size="small"
              sx={{ fontWeight: 600, letterSpacing: 0.3 }}
            >
              Clear all
            </Button>
          }
        />
        <CardContent sx={{ px: 2.5, py: 2.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            letterSpacing={0.6}
            sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase' }}
          >
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                fullWidth
                sx={fieldSizing}
                value={
                  filters.status_id
                    ? statusOptions.find((status) => status.id === filters.status_id) ?? null
                    : null
                }
                onOpen={() => onOpenStatusOptions?.()}
                loading={statusOptionsLoading}
                onChange={(_, value) => onFilterChange('status_id', value ? value.id : '')}
                options={statusOptions}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => <TextField {...params} label="Status" placeholder="All statuses" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={statusOptionsLoading ? 'Loading…' : 'No status found'}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                fullWidth
                sx={fieldSizing}
                value={
                  filters.assignee_id
                    ? usersList.find((user) => user.id === filters.assignee_id) ?? null
                    : null
                }
                onOpen={() => onOpenUsersList?.()}
                loading={usersListLoading}
                onChange={(_, value) => onFilterChange('assignee_id', value ? value.id : '')}
                options={usersList}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => <TextField {...params} label="Assignee" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={usersListLoading ? 'Loading…' : 'No assignee found'}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                fullWidth
                sx={fieldSizing}
                value={
                  filters.author_id
                    ? usersList.find((user) => user.id === filters.author_id) ?? null
                    : null
                }
                onOpen={() => onOpenUsersList?.()}
                loading={usersListLoading}
                onChange={(_, value) => onFilterChange('author_id', value ? value.id : '')}
                options={usersList}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => <TextField {...params} label="Created by" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={usersListLoading ? 'Loading…' : 'No author found'}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="No activity since (days)"
                type="number"
                value={noActivity.no_activity_since_days}
                onChange={(e) => onNoActivityChange(e.target.value)}
                sx={fieldSizing}
                InputProps={{
                  inputProps: { min: 1 },
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <SwapVertIcon color="action" sx={{ fontSize: 20, opacity: 0.85 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              letterSpacing={0.6}
              sx={{ textTransform: 'uppercase' }}
            >
              Payment and sort
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} >
              <FormControl fullWidth size="small" sx={fieldSizing}>
                <InputLabel id="lead-filter-payment">Payment status</InputLabel>
                <Select
                  labelId="lead-filter-payment"
                  value={filters.payment_status || ''}
                  onChange={(e) => onFilterChange('payment_status', e.target.value)}
                  label="Payment status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={fieldSizing}>
                <InputLabel id="lead-sort-by">Sort by</InputLabel>
                <Select
                  labelId="lead-sort-by"
                  value={sort.sort_by}
                  onChange={(e) => onSortChange({ ...sort, sort_by: e.target.value })}
                  label="Sort by"
                >
                  <MenuItem value="updated_at">Last updated</MenuItem>
                  <MenuItem value="created_at">Created at</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} sm={6} >
              <FormControl fullWidth size="small" sx={fieldSizing}>
                <InputLabel id="lead-sort-dir">Sort direction</InputLabel>
                <Select
                  labelId="lead-sort-dir"
                  value={sort.sort_direction}
                  onChange={(e) =>
                    onSortChange({ ...sort, sort_direction: e.target.value })
                  }
                  label="Sort direction"
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  );
};

export default LeadFilters;
